/**
 * PayoutHistoryTable
 * Monthly payout history with clickable rows for drill-down
 */

import EmptyState from '@components/ui/EmptyState';
import {
  type CreatorPayoutSummary,
  PAYOUT_STATUS_LABELS,
  type PayoutStatus,
} from '@parlay-picker/shared/creatorPayout';
import { formatCents, formatPeriodLabel } from '@parlay-picker/shared/formatting';
import { cn } from '@utils/cn';
import { Calendar, Loader2 } from 'lucide-react';
import type { ReactElement } from 'react';

interface PayoutHistoryTableProps {
  payouts: CreatorPayoutSummary[];
  loading: boolean;
  hasMore: boolean;
  onSelectPeriod: (periodLabel: string) => void;
  onLoadMore: () => void;
}

const statusStyles: Record<PayoutStatus, string> = {
  calculated: 'bg-theme-warning/10 text-theme-warning border-theme-warning/30',
  transferred: 'bg-theme-success/10 text-theme-success border-theme-success/30',
  failed: 'bg-theme-danger/10 text-theme-danger border-theme-danger/30',
};

export function PayoutHistoryTable({
  payouts,
  loading,
  hasMore,
  onSelectPeriod,
  onLoadMore,
}: PayoutHistoryTableProps): ReactElement {
  if (loading) {
    return (
      <div className="bg-theme-surface/50 rounded-lg border border-theme-border p-6">
        <div className="animate-pulse space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-10 bg-theme-border rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (payouts.length === 0) {
    return (
      <EmptyState
        icon={<Calendar className="w-8 h-8 text-theme-text-tertiary" />}
        title="No payout history"
        description="Earnings are calculated on the 1st of each month."
      />
    );
  }

  return (
    <div className="bg-theme-surface/50 rounded-lg overflow-hidden border border-theme-border">
      <div className="px-4 py-3 border-b border-theme-border">
        <h3 className="text-sm font-semibold text-theme-text">Payout History</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-theme-surface border-b border-theme-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                Period
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                Followers
              </th>
              <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                Eligible
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                Your Earnings
              </th>
              <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                Gross
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-theme-border">
            {payouts.map((payout) => (
              <tr
                key={payout.id}
                onClick={() => onSelectPeriod(payout.periodLabel)}
                className="hover:bg-theme-surface-hover cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 text-sm font-medium text-theme-text">
                  {formatPeriodLabel(payout.periodLabel)}
                </td>
                <td className="px-4 py-3 text-sm text-theme-text-secondary text-right">
                  {payout.totalFollowers}
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-sm text-theme-text-secondary text-right">
                  {payout.eligibleFollowers}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-right">
                  <span
                    className={
                      payout.creatorShareCents > 0
                        ? 'text-theme-success'
                        : 'text-theme-text-secondary'
                    }
                  >
                    {formatCents(payout.creatorShareCents)}
                  </span>
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-sm text-theme-text-secondary text-right">
                  {formatCents(payout.grossRevenueCents)}
                </td>
                <td className="px-4 py-3 text-right">
                  <span
                    className={cn(
                      'inline-flex px-2 py-0.5 text-xs font-medium rounded-full border',
                      statusStyles[payout.status] ?? '',
                    )}
                  >
                    {PAYOUT_STATUS_LABELS[payout.status] ?? payout.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <div className="flex justify-center py-3 border-t border-theme-border">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-theme-primary hover:text-theme-primary-hover transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Load more'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
